import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "firebase/auth";
import { t } from "i18next";
import {
  signInUserWithEmail,
  signOutUser,
  signupUserWithEmail,
  sendPasswordResetWithEmail,
  getUser,
  addUser as addUserService, // renombramos para diferenciarlo del método del store
  type UserData,
  type AddUserParameters,
  checkEmailVerified,
  getCurrentUser,
} from "@/services/auth";
import { FirebaseError } from "firebase/app";
import * as sentry from "@/services/sentry";
import { AUTH_ERRORS } from "@/common/errors";

export interface AuthState {
  user: User | null;
  userData: null | UserData;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithCurrentUser: () => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
  forgotPassword: (email: string) => Promise<void>;
  getUser: (email: string) => Promise<void>;
  addUser: ({
    email,
    firstname,
    lastname,
    password,
    occupationId,
  }: Omit<AddUserParameters, "fUid"> & { password: string }) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set): AuthState => ({
      user: null,
      userData: null,
      loading: false,
      error: null,
      signIn: async (email: string, password: string): Promise<void> => {
        set({ loading: true, error: null });
        try {
          const userCredential = await signInUserWithEmail({ email, password });
          const { user } = await getUser({ email });
          const isEmailVerified = await checkEmailVerified();
          if (!isEmailVerified) {
            throw new Error(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
          }
          sentry.signIn({
            id: user?.id,
            fuid: user?.f_uid,
            email: user?.email,
            fullName: `${user?.firstname} ${user?.lastname}`,
          });
          set({ user: userCredential.user, loading: false, userData: user });
        } catch (error: unknown) {
          if (error instanceof FirebaseError) {
            set({
              user: null,
              error: error.code,
              loading: false,
              userData: null,
            });
          } else if (error instanceof Error) {
            set({
              user: null,
              error: error.message,
              loading: false,
              userData: null,
            });
          } else {
            set({
              user: null,
              userData: null,
              error: t("error.unknown"),
              loading: false,
            });
          }
        }
      },
      signInWithCurrentUser: async (): Promise<void> => {
        set({ loading: true, error: null });
        try {
          const currentUser = await getCurrentUser();
          const isEmailVerified = await checkEmailVerified();
          if (!isEmailVerified) {
            throw new Error(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
          }
          const { user } = await getUser({ email: currentUser!.email! });
          sentry.signIn({
            id: user?.id,
            fuid: user?.f_uid,
            email: user?.email,
            fullName: `${user?.firstname} ${user?.lastname}`,
          });
          set({ user: currentUser, userData: user, loading: false });
        } catch (error: unknown) {
          let message: string;
          if (error instanceof FirebaseError) {
            message = error.code;
          } else if (error instanceof Error) {
            message = error.message;
          } else {
            message = t("error.unknown");
          }
          set({
            user: null,
            userData: null,
            error: message,
            loading: false,
          });
        }
      },
      signOut: async (): Promise<void> => {
        set({ loading: true, error: null });
        try {
          await signOutUser();
          sentry.signOut();
          set({ user: null, loading: false, userData: null });
        } catch (error: unknown) {
          if (error instanceof Error) {
            set({ user: null, error: error.message, loading: false });
          } else {
            set({
              user: null,
              error: t("error.unknown"),
              loading: false,
            });
          }
        }
      },

      forgotPassword: async (email: string): Promise<void> => {
        set({ loading: true, error: null });
        try {
          await sendPasswordResetWithEmail({ email });
          set({ user: null, loading: false });
        } catch (error: unknown) {
          if (error instanceof Error) {
            set({ user: null, error: error.message, loading: false });
          } else {
            set({
              user: null,
              error: t("error.unknown"),
              loading: false,
            });
          }
        }
      },

      setUser: (user: User | null): void => {
        set({ user });
      },

      getUser: async (email: string): Promise<void> => {
        set({ loading: true, error: null });
        try {
          const { user } = await getUser({ email });
          sentry.signIn({
            id: user?.id,
            fuid: user?.f_uid,
            email: user?.email,
            fullName: `${user?.firstname} ${user?.lastname}`,
          });
          set({ userData: user, loading: false });
        } catch (error: unknown) {
          if (error instanceof Error) {
            set({ userData: null, error: error.message, loading: false });
          } else {
            set({
              user: null,
              error: t("error.unknown"),
              loading: false,
            });
          }
        }
      },

      addUser: async ({
        email,
        firstname,
        lastname,
        password,
        occupationId,
      }: Omit<AddUserParameters, "fUid"> & {
        password: string;
      }): Promise<void> => {
        set({ loading: true, error: null });
        try {
          const userCredential = await signupUserWithEmail({
            email,
            password,
          });
          const fUid = userCredential.user.uid;
          await addUserService({
            email,
            firstname,
            lastname,
            fUid,
            occupationId,
          });
          set({ user: null, error: null, loading: false });
        } catch (error: unknown) {
          if (error instanceof Error) {
            set({ loading: false, error: error.message, user: null });
          } else {
            set({ loading: false, error: t("error.unknown"), user: null });
          }
        }
      },
    }),
    {
      name: "auth-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { error, ...rest } = state;
        return rest;
      },
    }
  )
);
